import { doc, getDoc, setDoc, updateDoc, increment, deleteDoc } from "firebase/firestore";
import { db } from "./firebase";

export type DietType = "none" | "vegetarian" | "vegan" | "pescatarian" | "keto" | "paleo";
export type SubscriptionTier = "free" | "premium" | "trial";

// Feature types for usage tracking
export type UsageFeature = "chat" | "mealBuilder" | "mealPlanner" | "menuDecoder" | "groceryOptimizer" | "recipeTransformer";

// Promo code types
export type PromoCodeType = "premium" | "trial" | "bonus";

export interface PromoCode {
  code: string;
  type: PromoCodeType;
  // For trial/premium
  durationDays?: number;
  // For bonus - extra uses to add
  bonusUsage?: Partial<Record<UsageFeature, number>>;
  // Limits
  maxRedemptions?: number;
  currentRedemptions: number;
  expiresAt?: string;
  // Metadata
  isActive: boolean;
  createdAt: string;
  description?: string;
}

// Free tier limits (per month)
export const FREE_TIER_LIMITS: Record<UsageFeature, number> = {
  chat: 10,
  mealBuilder: 5,
  mealPlanner: 0, // Premium only
  menuDecoder: 2,
  groceryOptimizer: 0, // Premium only
  recipeTransformer: 3,
};

// Feature display names for UI
export const FEATURE_NAMES: Record<UsageFeature, string> = {
  chat: "Chat Questions",
  mealBuilder: "Meal Builder",
  mealPlanner: "Meal Planner",
  menuDecoder: "Menu Decoder",
  groceryOptimizer: "Grocery Optimizer",
  recipeTransformer: "Recipe Transformer",
};

export interface MonthlyUsage {
  chat: number;
  mealBuilder: number;
  mealPlanner: number;
  menuDecoder: number;
  groceryOptimizer: number;
  recipeTransformer: number;
  resetMonth: string; // Format: "YYYY-MM"
}

export const DIET_OPTIONS: { id: DietType; label: string; icon: string }[] = [
  { id: "none", label: "No Restriction", icon: "" },
  { id: "vegetarian", label: "Vegetarian", icon: "" },
  { id: "vegan", label: "Vegan", icon: "" },
  { id: "pescatarian", label: "Pescatarian", icon: "" },
  { id: "keto", label: "Keto", icon: "" },
  { id: "paleo", label: "Paleo", icon: "" },
];

export const ALLERGY_OPTIONS: { id: string; label: string; icon: string }[] = [
  { id: "gluten", label: "Gluten", icon: "" },
  { id: "dairy", label: "Dairy", icon: "" },
  { id: "nuts", label: "Tree Nuts", icon: "" },
  { id: "peanuts", label: "Peanuts", icon: "" },
  { id: "shellfish", label: "Shellfish", icon: "" },
  { id: "eggs", label: "Eggs", icon: "" },
  { id: "soy", label: "Soy", icon: "" },
  { id: "fish", label: "Fish", icon: "" },
];

export interface UserStats {
  totalQueries: number;
  totalMealsAnalyzed: number;
  totalRecipesAnalyzed: number;
  totalMenusDecoded: number;
  totalGroceryListsOptimized: number;
  totalPhotosAnalyzed: number;
  streakDays: number;
  lastActiveDate: string;
  topFoodsSearched: Record<string, number>;
  joinedAt: string;
  badges: string[];
  // Dietary preferences
  dietType: DietType;
  allergies: string[];
  // Subscription & Usage
  subscriptionTier: SubscriptionTier;
  subscriptionExpiry?: string; // ISO date for trial/paid premium expiry
  monthlyUsage: MonthlyUsage;
  // Promo codes
  redeemedCodes?: string[];
  bonusUsage?: Partial<Record<UsageFeature, number>>; // Extra uses from promo codes
  // Stripe
  stripeCustomerId?: string;
  subscriptionCancelAtPeriodEnd?: boolean;
  subscriptionPeriodEnd?: string; // ISO date when current period ends
}

// Get current month string
function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

const defaultMonthlyUsage: MonthlyUsage = {
  chat: 0,
  mealBuilder: 0,
  mealPlanner: 0,
  menuDecoder: 0,
  groceryOptimizer: 0,
  recipeTransformer: 0,
  resetMonth: getCurrentMonth(),
};

const defaultStats: UserStats = {
  totalQueries: 0,
  totalMealsAnalyzed: 0,
  totalRecipesAnalyzed: 0,
  totalMenusDecoded: 0,
  totalGroceryListsOptimized: 0,
  totalPhotosAnalyzed: 0,
  streakDays: 0,
  lastActiveDate: "",
  topFoodsSearched: {},
  joinedAt: new Date().toISOString(),
  badges: [],
  dietType: "none",
  allergies: [],
  subscriptionTier: "free",
  monthlyUsage: defaultMonthlyUsage,
};

export async function getUserStats(userId: string): Promise<UserStats> {
  const docRef = doc(db, "userStats", userId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data() as UserStats;
  }

  // Initialize new user stats
  await setDoc(docRef, defaultStats);
  return defaultStats;
}

export async function recordQuery(userId: string): Promise<void> {
  const docRef = doc(db, "userStats", userId);
  const today = new Date().toISOString().split("T")[0];

  try {
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data() as UserStats;
      const lastDate = data.lastActiveDate;

      // Check streak
      let newStreak = data.streakDays;
      if (lastDate) {
        const lastDateObj = new Date(lastDate);
        const todayObj = new Date(today);
        const diffDays = Math.floor((todayObj.getTime() - lastDateObj.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays === 1) {
          newStreak += 1;
        } else if (diffDays > 1) {
          newStreak = 1;
        }
        // If same day, streak stays the same
      } else {
        newStreak = 1;
      }

      await updateDoc(docRef, {
        totalQueries: increment(1),
        lastActiveDate: today,
        streakDays: newStreak,
      });
    } else {
      await setDoc(docRef, {
        ...defaultStats,
        totalQueries: 1,
        lastActiveDate: today,
        streakDays: 1,
        joinedAt: new Date().toISOString(),
      });
    }
  } catch (error) {
    console.error("Error recording query:", error);
  }
}

export async function recordMealAnalysis(userId: string): Promise<void> {
  const docRef = doc(db, "userStats", userId);
  try {
    await updateDoc(docRef, {
      totalMealsAnalyzed: increment(1),
    });
  } catch (error) {
    console.error("Error recording meal analysis:", error);
  }
}

export async function recordRecipeAnalysis(userId: string): Promise<void> {
  const docRef = doc(db, "userStats", userId);
  try {
    await updateDoc(docRef, {
      totalRecipesAnalyzed: increment(1),
    });
  } catch (error) {
    console.error("Error recording recipe analysis:", error);
  }
}

export async function recordMenuDecode(userId: string): Promise<void> {
  const docRef = doc(db, "userStats", userId);
  try {
    await updateDoc(docRef, {
      totalMenusDecoded: increment(1),
    });
  } catch (error) {
    console.error("Error recording menu decode:", error);
  }
}

export async function recordGroceryOptimization(userId: string): Promise<void> {
  const docRef = doc(db, "userStats", userId);
  try {
    await updateDoc(docRef, {
      totalGroceryListsOptimized: increment(1),
    });
  } catch (error) {
    console.error("Error recording grocery optimization:", error);
  }
}

export async function recordPhotoAnalysis(userId: string): Promise<void> {
  const docRef = doc(db, "userStats", userId);
  try {
    await updateDoc(docRef, {
      totalPhotosAnalyzed: increment(1),
    });
  } catch (error) {
    console.error("Error recording photo analysis:", error);
  }
}

export async function recordFoodSearch(userId: string, foods: string[]): Promise<void> {
  if (!foods.length) return;

  const docRef = doc(db, "userStats", userId);
  try {
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data() as UserStats;
      const topFoods = { ...data.topFoodsSearched };

      // Increment count for each food
      for (const food of foods) {
        const normalizedFood = food.toLowerCase().trim();
        if (normalizedFood) {
          topFoods[normalizedFood] = (topFoods[normalizedFood] || 0) + 1;
        }
      }

      await updateDoc(docRef, { topFoodsSearched: topFoods });
    }
  } catch (error) {
    console.error("Error recording food search:", error);
  }
}

export function getTopSearchedFoods(stats: UserStats, limit: number = 5): { food: string; count: number }[] {
  const entries = Object.entries(stats.topFoodsSearched || {});
  return entries
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([food, count]) => ({ food, count }));
}

export async function addBadge(userId: string, badge: string): Promise<void> {
  const docRef = doc(db, "userStats", userId);
  try {
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data() as UserStats;
      if (!data.badges.includes(badge)) {
        await updateDoc(docRef, {
          badges: [...data.badges, badge],
        });
      }
    }
  } catch (error) {
    console.error("Error adding badge:", error);
  }
}

export async function updateDietaryPreferences(
  userId: string,
  dietType: DietType,
  allergies: string[]
): Promise<void> {
  const docRef = doc(db, "userStats", userId);
  try {
    await updateDoc(docRef, {
      dietType,
      allergies,
    });
  } catch (error) {
    console.error("Error updating dietary preferences:", error);
    throw error;
  }
}

// Badge definitions
export const BADGES = {
  // Getting Started
  first_query: { id: "first_query", name: "First Steps", icon: "", description: "Made your first food query" },
  first_photo: { id: "first_photo", name: "Photographer", icon: "", description: "Analyzed your first photo" },

  // Streaks
  streak_3: { id: "streak_3", name: "On a Roll", icon: "", description: "3 day streak" },
  streak_7: { id: "streak_7", name: "Week Warrior", icon: "", description: "7 day streak" },
  streak_14: { id: "streak_14", name: "Streak Master", icon: "", description: "14 day streak" },
  streak_30: { id: "streak_30", name: "Monthly Champion", icon: "", description: "30 day streak" },

  // Queries
  queries_50: { id: "queries_50", name: "Curious Mind", icon: "", description: "Asked 50 questions" },
  queries_100: { id: "queries_100", name: "Century Club", icon: "", description: "Asked 100 questions" },

  // Feature Usage
  meals_10: { id: "meals_10", name: "Meal Master", icon: "", description: "Analyzed 10 meals" },
  recipes_5: { id: "recipes_5", name: "Recipe Guru", icon: "", description: "Transformed 5 recipes" },
  recipes_10: { id: "recipes_10", name: "Recipe Alchemist", icon: "", description: "Transformed 10 recipes" },
  menus_5: { id: "menus_5", name: "Menu Navigator", icon: "", description: "Decoded 5 restaurant menus" },
  grocery_5: { id: "grocery_5", name: "Smart Shopper", icon: "", description: "Optimized 5 grocery lists" },

  // Explorer
  explorer: { id: "explorer", name: "Explorer", icon: "", description: "Used all 5 main features" },

  // Time-based
  night_owl: { id: "night_owl", name: "Night Owl", icon: "", description: "Used the app after midnight" },
  early_bird: { id: "early_bird", name: "Early Bird", icon: "", description: "Used the app before 6am" },

  // Special
  low_gi_champion: { id: "low_gi_champion", name: "Low GI Champion", icon: "", description: "Built 5 low-GI meals in a row" },
};

export async function checkAndAwardBadges(userId: string, stats: UserStats): Promise<string[]> {
  const newBadges: string[] = [];

  // First steps
  if (stats.totalQueries >= 1 && !stats.badges.includes("first_query")) {
    await addBadge(userId, "first_query");
    newBadges.push("first_query");
  }

  // First photo
  if ((stats.totalPhotosAnalyzed || 0) >= 1 && !stats.badges.includes("first_photo")) {
    await addBadge(userId, "first_photo");
    newBadges.push("first_photo");
  }

  // Streak badges
  if (stats.streakDays >= 3 && !stats.badges.includes("streak_3")) {
    await addBadge(userId, "streak_3");
    newBadges.push("streak_3");
  }

  if (stats.streakDays >= 7 && !stats.badges.includes("streak_7")) {
    await addBadge(userId, "streak_7");
    newBadges.push("streak_7");
  }

  if (stats.streakDays >= 14 && !stats.badges.includes("streak_14")) {
    await addBadge(userId, "streak_14");
    newBadges.push("streak_14");
  }

  if (stats.streakDays >= 30 && !stats.badges.includes("streak_30")) {
    await addBadge(userId, "streak_30");
    newBadges.push("streak_30");
  }

  // Query badges
  if (stats.totalQueries >= 50 && !stats.badges.includes("queries_50")) {
    await addBadge(userId, "queries_50");
    newBadges.push("queries_50");
  }

  if (stats.totalQueries >= 100 && !stats.badges.includes("queries_100")) {
    await addBadge(userId, "queries_100");
    newBadges.push("queries_100");
  }

  // Meal badges
  if (stats.totalMealsAnalyzed >= 10 && !stats.badges.includes("meals_10")) {
    await addBadge(userId, "meals_10");
    newBadges.push("meals_10");
  }

  // Recipe badges
  if (stats.totalRecipesAnalyzed >= 5 && !stats.badges.includes("recipes_5")) {
    await addBadge(userId, "recipes_5");
    newBadges.push("recipes_5");
  }

  if (stats.totalRecipesAnalyzed >= 10 && !stats.badges.includes("recipes_10")) {
    await addBadge(userId, "recipes_10");
    newBadges.push("recipes_10");
  }

  // Menu badges
  if ((stats.totalMenusDecoded || 0) >= 5 && !stats.badges.includes("menus_5")) {
    await addBadge(userId, "menus_5");
    newBadges.push("menus_5");
  }

  // Grocery badges
  if ((stats.totalGroceryListsOptimized || 0) >= 5 && !stats.badges.includes("grocery_5")) {
    await addBadge(userId, "grocery_5");
    newBadges.push("grocery_5");
  }

  // Explorer badge - used all main features
  const usedAllFeatures =
    stats.totalQueries >= 1 &&
    stats.totalMealsAnalyzed >= 1 &&
    stats.totalRecipesAnalyzed >= 1 &&
    (stats.totalMenusDecoded || 0) >= 1 &&
    (stats.totalGroceryListsOptimized || 0) >= 1;

  if (usedAllFeatures && !stats.badges.includes("explorer")) {
    await addBadge(userId, "explorer");
    newBadges.push("explorer");
  }

  // Time-based badges (check current hour)
  const currentHour = new Date().getHours();
  if (currentHour >= 0 && currentHour < 5 && !stats.badges.includes("night_owl")) {
    await addBadge(userId, "night_owl");
    newBadges.push("night_owl");
  }

  if (currentHour >= 5 && currentHour < 6 && !stats.badges.includes("early_bird")) {
    await addBadge(userId, "early_bird");
    newBadges.push("early_bird");
  }

  return newBadges;
}

// Helper to check for low GI champion (called from MealBuilder)
export async function checkLowGiChampion(userId: string, isLowGi: boolean): Promise<string | null> {
  const docRef = doc(db, "userStats", userId);
  try {
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      let consecutiveLowGi = data.consecutiveLowGiMeals || 0;

      if (isLowGi) {
        consecutiveLowGi++;
        await updateDoc(docRef, { consecutiveLowGiMeals: consecutiveLowGi });

        if (consecutiveLowGi >= 5 && !data.badges?.includes("low_gi_champion")) {
          await addBadge(userId, "low_gi_champion");
          return "low_gi_champion";
        }
      } else {
        await updateDoc(docRef, { consecutiveLowGiMeals: 0 });
      }
    }
  } catch (error) {
    console.error("Error checking low GI champion:", error);
  }
  return null;
}

// ============================================
// USAGE TRACKING & LIMITS
// ============================================

export interface UsageLimitResult {
  allowed: boolean;
  currentUsage: number;
  limit: number;
  isPremiumOnly: boolean;
}

// Helper to check if subscription is active
export function isSubscriptionActive(stats: UserStats): boolean {
  const tier = stats.subscriptionTier;
  if (tier === "free") return false;
  if (tier === "premium" || tier === "trial") {
    // Check expiry if set
    if (stats.subscriptionExpiry) {
      return new Date(stats.subscriptionExpiry) > new Date();
    }
    // No expiry = permanent premium
    return tier === "premium";
  }
  return false;
}

// Check if user can use a feature (doesn't increment counter)
export async function checkUsageLimit(userId: string, feature: UsageFeature): Promise<UsageLimitResult> {
  const docRef = doc(db, "userStats", userId);
  const docSnap = await getDoc(docRef);

  const currentMonth = getCurrentMonth();
  let stats = defaultStats;

  if (docSnap.exists()) {
    stats = docSnap.data() as UserStats;
  }

  // Premium/Trial users have unlimited access (if not expired)
  if (isSubscriptionActive(stats)) {
    return { allowed: true, currentUsage: 0, limit: Infinity, isPremiumOnly: false };
  }

  // Calculate effective limit (base + bonus from promo codes)
  const baseLimit = FREE_TIER_LIMITS[feature];
  const bonusLimit = stats.bonusUsage?.[feature] || 0;
  const effectiveLimit = baseLimit + bonusLimit;

  // Premium-only feature (limit = 0 and no bonus)
  if (effectiveLimit === 0) {
    return { allowed: false, currentUsage: 0, limit: 0, isPremiumOnly: true };
  }

  // Check if we need to reset monthly usage
  let monthlyUsage = stats.monthlyUsage || defaultMonthlyUsage;
  if (monthlyUsage.resetMonth !== currentMonth) {
    // New month - reset counters locally for check (actual reset happens on record)
    monthlyUsage = { ...defaultMonthlyUsage, resetMonth: currentMonth };
  }

  const currentUsage = monthlyUsage[feature] || 0;

  return {
    allowed: currentUsage < effectiveLimit,
    currentUsage,
    limit: effectiveLimit,
    isPremiumOnly: false,
  };
}

// Record usage of a feature (increments counter)
export async function recordFeatureUsage(userId: string, feature: UsageFeature): Promise<UsageLimitResult> {
  const docRef = doc(db, "userStats", userId);
  const currentMonth = getCurrentMonth();

  try {
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      // Create new user with this usage
      const newUsage = { ...defaultMonthlyUsage, [feature]: 1 };
      await setDoc(docRef, { ...defaultStats, monthlyUsage: newUsage });
      return { allowed: true, currentUsage: 1, limit: FREE_TIER_LIMITS[feature], isPremiumOnly: false };
    }

    const stats = docSnap.data() as UserStats;

    // Premium/Trial users - just record, no limits (if not expired)
    if (isSubscriptionActive(stats)) {
      let monthlyUsage = stats.monthlyUsage || defaultMonthlyUsage;
      if (monthlyUsage.resetMonth !== currentMonth) {
        monthlyUsage = { ...defaultMonthlyUsage, resetMonth: currentMonth };
      }
      monthlyUsage = { ...monthlyUsage, [feature]: (monthlyUsage[feature] || 0) + 1 };
      await updateDoc(docRef, { monthlyUsage });
      return { allowed: true, currentUsage: monthlyUsage[feature], limit: Infinity, isPremiumOnly: false };
    }

    // Calculate effective limit (base + bonus)
    const baseLimit = FREE_TIER_LIMITS[feature];
    const bonusLimit = stats.bonusUsage?.[feature] || 0;
    const effectiveLimit = baseLimit + bonusLimit;

    // Premium-only feature with no bonus
    if (effectiveLimit === 0) {
      return { allowed: false, currentUsage: 0, limit: 0, isPremiumOnly: true };
    }

    // Check and potentially reset monthly usage
    let monthlyUsage = stats.monthlyUsage || defaultMonthlyUsage;
    if (monthlyUsage.resetMonth !== currentMonth) {
      // Reset for new month
      monthlyUsage = { ...defaultMonthlyUsage, resetMonth: currentMonth };
    }

    const currentUsage = monthlyUsage[feature] || 0;

    // Check limit
    if (currentUsage >= effectiveLimit) {
      return { allowed: false, currentUsage, limit: effectiveLimit, isPremiumOnly: false };
    }

    // Increment and save
    const newUsage = currentUsage + 1;
    monthlyUsage = { ...monthlyUsage, [feature]: newUsage };
    await updateDoc(docRef, { monthlyUsage });

    return { allowed: true, currentUsage: newUsage, limit: effectiveLimit, isPremiumOnly: false };
  } catch (error) {
    console.error("Error recording feature usage:", error);
    // On error, allow the action (fail open for UX)
    return { allowed: true, currentUsage: 0, limit: FREE_TIER_LIMITS[feature], isPremiumOnly: false };
  }
}

// Extended usage stats interface for display
export interface UsageStatsResult extends MonthlyUsage {
  tier: SubscriptionTier;
  subscriptionExpiry?: string;
  bonusUsage?: Partial<Record<UsageFeature, number>>;
  isActive: boolean;
}

// Get current usage stats for display
export async function getUsageStats(userId: string): Promise<UsageStatsResult> {
  const docRef = doc(db, "userStats", userId);
  const currentMonth = getCurrentMonth();

  try {
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return { ...defaultMonthlyUsage, tier: "free", isActive: false };
    }

    const stats = docSnap.data() as UserStats;
    let monthlyUsage = stats.monthlyUsage || defaultMonthlyUsage;

    // Reset if new month
    if (monthlyUsage.resetMonth !== currentMonth) {
      monthlyUsage = { ...defaultMonthlyUsage, resetMonth: currentMonth };
      await updateDoc(docRef, { monthlyUsage });
    }

    const tier = stats.subscriptionTier || "free";
    const isActive = isSubscriptionActive(stats);

    return {
      ...monthlyUsage,
      tier,
      subscriptionExpiry: stats.subscriptionExpiry,
      bonusUsage: stats.bonusUsage,
      isActive,
    };
  } catch (error) {
    console.error("Error getting usage stats:", error);
    return { ...defaultMonthlyUsage, tier: "free", isActive: false };
  }
}

// Upgrade user to premium (called after payment processing)
export async function upgradeToPremium(userId: string, expiresAt?: string): Promise<void> {
  const docRef = doc(db, "userStats", userId);
  try {
    const updateData: Partial<UserStats> = { subscriptionTier: "premium" };
    if (expiresAt) {
      updateData.subscriptionExpiry = expiresAt;
    }
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error("Error upgrading to premium:", error);
    throw error;
  }
}

// Delete all user data from Firestore (for GDPR compliance)
export async function deleteUserData(userId: string): Promise<void> {
  const docRef = doc(db, "userStats", userId);
  try {
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting user data:", error);
    throw error;
  }
}
